import type { ReactNode } from 'react'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils'
import { SkeletonRows } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/ui/empty-state'

/**
 * Một định nghĩa cột, hai cách render:
 * - `≥ md`: `<table>` như cũ, mọi cột hiện đủ theo đúng thứ tự khai báo.
 * - `< md`: mỗi hàng thành một thẻ. `priority` quyết định vị trí trên thẻ:
 *   - `primary`   — dòng tiêu đề của thẻ (thường đã tự chứa dòng phụ bên trong `cell`)
 *   - `secondary` — dòng phụ ngay dưới tiêu đề, chữ nhạt hơn
 *   - `meta`      — xếp vào lưới 2 cột nhãn/giá trị (mặc định cho phần lớn cột)
 *   - `action`    — hàng nút ở đáy thẻ
 *
 * `cell` dùng chung cho cả hai chế độ; truyền `mobileCell` khi thẻ cần bố cục khác
 * hẳn với bảng (ví dụ hàng nút xếp dọc thay vì nằm ngang).
 */
export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  mobileCell?: (row: T) => ReactNode
  priority?: 'primary' | 'secondary' | 'meta' | 'action'
  align?: 'left' | 'right' | 'center'
  /** Nhãn hiển thị trong lưới meta ở thẻ mobile; mặc định dùng `header`. */
  mobileLabel?: ReactNode
  /** Bỏ hẳn cột này trên thẻ mobile (dữ liệu đã lặp lại ở cột khác). */
  hideOnMobile?: boolean
  headerClassName?: string
  cellClassName?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  rowKey: (row: T, index?: number) => string
  loading?: boolean
  loadingLabel?: string
  emptyLabel?: string
  onRowClick?: (row: T) => void
  className?: string
  /** className cho <table> ở chế độ desktop */
  tableClassName?: string
  /** className cho từng thẻ ở chế độ mobile */
  cardClassName?: string
  /**
   * className tính theo từng hàng — cho các bảng cần đánh dấu trạng thái của
   * riêng hàng đó (đang chọn, quá hạn, bị khoá…). Áp cho CẢ `<tr>` ở desktop
   * lẫn thẻ ở mobile, nên trạng thái không biến mất khi đổi chế độ.
   */
  rowClassName?: (row: T) => string | undefined
  /** Cột STT đầu bảng / số ở góc thẻ — mặc định có; tắt cho bảng không phải danh sách bản ghi. */
  index?: boolean
  /** Số của hàng đầu — danh sách phân trang truyền offset + 1. */
  indexStart?: number
}

/** Ô số thứ tự — dùng chung cho DataTable và bảng tự viết (td / th của bảng đó lo khoảng đệm). */
export const INDEX_CELL = 'w-px whitespace-nowrap text-right tabular-nums text-muted'
export const INDEX_HEADER = 'STT'

/** Số thứ tự ở đầu thẻ / hàng của danh sách dạng thẻ (điện thoại, ngăn chi tiết…). */
export function IndexBadge({ n, className }: { n: number; className?: string }) {
  return (
    <span className={cn('min-w-6 shrink-0 rounded-md bg-paperSoft px-1.5 py-0.5 text-center text-xs font-semibold tabular-nums text-inkSoft', className)}>
      {n}
    </span>
  )
}

const alignClass = (a?: 'left' | 'right' | 'center') =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left'

export default function DataTable<T>({
  columns,
  data,
  rowKey,
  loading = false,
  loadingLabel = 'Đang tải…',
  emptyLabel = 'Không có dữ liệu phù hợp',
  onRowClick,
  className,
  tableClassName,
  cardClassName,
  rowClassName,
  index: showIndex = true,
  indexStart = 1,
}: DataTableProps<T>) {
  const isMobile = useIsMobile()

  // Skeleton giữ đúng chiều cao của bảng nên nội dung không nhảy một nhịp khi
  // dữ liệu về — spinner căn giữa thì có.
  if (loading) {
    return (
      <div role="status" aria-busy="true" aria-label={loadingLabel}>
        <SkeletonRows rows={5} className={className} />
      </div>
    )
  }

  if (data.length === 0) {
    return <EmptyState title={emptyLabel} className={className} />
  }

  // ── Mobile: mỗi hàng một thẻ ─────────────────────────────────────
  if (isMobile) {
    const primaryCols = columns.filter((c) => c.priority === 'primary' && !c.hideOnMobile)
    const secondaryCols = columns.filter((c) => c.priority === 'secondary' && !c.hideOnMobile)
    const metaCols = columns.filter(
      (c) => (!c.priority || c.priority === 'meta') && !c.hideOnMobile,
    )
    const actionCols = columns.filter((c) => c.priority === 'action' && !c.hideOnMobile)

    // Xếp thẻ theo lưới khi còn chỗ. Ở chế độ mobile, bề rộng chạy từ 320px
    // (điện thoại) tới 1366px (iPad Pro nằm ngang) — để một cột suốt dải đó
    // thì trên máy tính bảng mỗi thẻ bị kéo dãn hết bề ngang, lưới nhãn/giá
    // trị hai cột giãn ra thưa thớt và mắt phải quét quãng dài vô ích.
    //
    // Dưới 640px vẫn là danh sách xếp chồng, ngăn nhau bằng đường kẻ như cũ.
    // Từ 640px trở lên chuyển thành lưới có khe hở, nên mỗi thẻ phải tự có
    // viền và nền của nó.
    return (
      <div
        className={cn(
          'divide-y divide-rule/60',
          'sm:grid sm:grid-cols-2 sm:gap-3 sm:divide-y-0 xl:grid-cols-3',
          className,
        )}
      >
        {data.map((row, index) => {
          const key = rowKey(row, index)
          // Thẻ bấm được là <div role="button">, KHÔNG phải <button>: trong thẻ thường có
          // nút / link / ô chọn riêng (In, Sửa…) và <button> lồng <button> là HTML sai.
          return (
            <div
              key={key}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onKeyDown={
                onRowClick
                  ? (e) => {
                      // Chỉ khi chính thẻ đang được focus — Enter trên nút con là việc của nút đó
                      if (e.target !== e.currentTarget) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onRowClick(row)
                      }
                    }
                  : undefined
              }
              className={cn(
                'w-full text-left p-3.5 space-y-2.5',
                // Ở chế độ lưới thẻ đứng rời nhau nên cần viền riêng; ở chế độ
                // xếp chồng thì đường kẻ của khối cha đã làm việc đó.
                'sm:rounded-xl sm:border sm:border-rule sm:bg-raise',
                onRowClick &&
                  'motion-state cursor-pointer active:bg-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
                cardClassName,
                rowClassName?.(row),
              )}
            >
              {(primaryCols.length > 0 || showIndex) && (
                <div className="flex items-start gap-2.5">
                  {showIndex && <IndexBadge n={indexStart + index} className="mt-px" />}
                  <div className="min-w-0 flex-1 space-y-0.5">
                    {primaryCols.map((c) => (
                      <div key={c.key}>{(c.mobileCell ?? c.cell)(row)}</div>
                    ))}
                  </div>
                </div>
              )}

              {secondaryCols.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                  {secondaryCols.map((c) => (
                    <div key={c.key}>{(c.mobileCell ?? c.cell)(row)}</div>
                  ))}
                </div>
              )}

              {metaCols.length > 0 && (
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 pt-2 border-t border-rule/60">
                  {metaCols.map((c) => (
                    <div key={c.key} className="min-w-0">
                      <div className="text-xs text-muted">{c.mobileLabel ?? c.header}</div>
                      <div className="mt-0.5 text-sm">{(c.mobileCell ?? c.cell)(row)}</div>
                    </div>
                  ))}
                </div>
              )}

              {actionCols.length > 0 && (
                <div
                  className="flex flex-wrap items-center gap-2 pt-2.5 border-t border-rule/60"
                  onClick={(e) => e.stopPropagation()}
                >
                  {actionCols.map((c) => (
                    <div key={c.key} className="flex-1 min-w-[120px]">
                      {(c.mobileCell ?? c.cell)(row)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  // ── Desktop: bảng như cũ ──────────────────────────────────────────
  // `relative`: phần tử absolute trong ô (vd. tiêu đề cột `sr-only`) nằm trong vùng cuộn — không thì lọt ra
  // ngoài, kéo rộng cả trang (cuộn ngang).
  return (
    <div className={cn('relative overflow-x-auto', className)}>
      <table className={cn('w-full text-xs text-left', tableClassName)}>
        <thead className="bg-paper border-b border-rule text-muted">
          <tr>
            {showIndex && <th className={cn('py-3 pl-3 pr-1', INDEX_CELL)}>{INDEX_HEADER}</th>}
            {columns.map((c) => (
              <th
                key={c.key}
                className={cn('py-3 px-3', alignClass(c.align), c.headerClassName)}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-rule/60">
          {data.map((row, index) => {
            const key = rowKey(row, index)
            return (
              <tr
                key={key}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  'motion-state hover:bg-paper/50',
                  onRowClick && 'cursor-pointer',
                  rowClassName?.(row),
                )}
              >
                {showIndex && <td className={cn('py-3 pl-3 pr-1', INDEX_CELL)}>{indexStart + index}</td>}
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={cn('py-3 px-3', alignClass(c.align), c.cellClassName)}
                  >
                    {c.cell(row)}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
